/* eslint-disable react/react-in-jsx-scope */
import { useEffect, useMemo, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import remarkGfm from 'remark-gfm'
import { aiCleanupPublicacoesConversation, aiCreateConversation, aiExtractLatestText, aiGetConversationMessages, aiListConversations, aiSendMessage, aiSendPublicacoesMessage, aiSendQualificacaoMessage, aiWaitForQualificacaoResult, aiWaitForResult } from '../../../helpers/ai_helper'
import './chatWidgetControlled.css'

const STORAGE_KEY = 'AI_CHAT_HISTORY_V2'
const JOB_KEY = 'AI_CHAT_JOB_ID_V2'
const GUEST_ID_KEY = 'AI_GUEST_USER_ID'

const ChatWidgetControlled = () => {
  const [isVisible, setIsVisible] = useState(false)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [abortCtrl, setAbortCtrl] = useState(null)
  const [draftText, setDraftText] = useState('')
  const [thread, setThread] = useState([])
  const [jobId, setJobId] = useState('')
  const [conversations, setConversations] = useState([])
  const [activeConversationId, setActiveConversationId] = useState('')
  const [userId, setUserId] = useState('')
  const [isLoadingConvs, setIsLoadingConvs] = useState(false)
  const [attachedFile, setAttachedFile] = useState(null)
  const [processoContext, setProcessoContext] = useState(null)

  const location = useLocation()
  const { processoFull } = useSelector(state => ({
    processoFull: state.Processo?.processos || null,
  }))

  const endRef = useRef(null)
  const inputRef = useRef(null)
  const fileInputRef = useRef(null)

  const title = useMemo(() => 'Blops AI', [])


  const MAX_WAIT_MS = useMemo(() => {
    const fromEnv = Number(300000)
    return Number.isFinite(fromEnv) && fromEnv > 0 ? fromEnv : 300000
  }, [])

  const primaryColor = 'var(--bs-primary, #556ee6)'

  useEffect(() => {
    const isQualificacaoPage = location.pathname.includes('/qualificacao-detail/')
    
    if (isQualificacaoPage && processoFull) { 
      const qualificacaoId = location.pathname.split('/qualificacao-detail/')[1]
      
      setProcessoContext({
        qualificacaoId: qualificacaoId || '',
        numeroCNJ: processoFull.NumeroCNJ || '',
        reclamante: processoFull.Lead?.Reclamante || '',
        reuRazaoSocial: processoFull.Lead?.ReuRazaoSocial || '',
        valorCausa: processoFull.Lead?.ValorCausa || '',
        tribunal: processoFull.Lead?.Tribunal || '',
        vara: processoFull.Lead?.Vara || '',
        esfera: processoFull.Lead?.Esfera || '',
        dataAjuizamento: processoFull.Lead?.DataAjuizamento || '',
        objetoSegurado: processoFull.Lead?.ObjetoSegurado || '',
        tipoLead: processoFull.Lead?.TipoLead || '',
        situacaoLead: processoFull.Lead?.SituacaoLead || '',
        status: processoFull.Lead?.Status || '',
        observacao: processoFull.Observacao || ''
      })
    } else {
      setProcessoContext(null)
    }
  }, [location.pathname, processoFull])

  useEffect(() => {
    if (!isVisible) return
    try {
      const raw = window.sessionStorage.getItem(STORAGE_KEY)
      if (raw) {
        const parsed = JSON.parse(raw)
        if (Array.isArray(parsed) && parsed.length) {
          setThread(parsed)
          return
        }
      }
    } catch (_) {}

  }, [isVisible])

  useEffect(() => {
    try {
      const raw = localStorage.getItem('authUser') || window.sessionStorage.getItem('authUser')
      if (raw) {
        const obj = JSON.parse(raw)
        const idCandidate = (obj?.id ?? obj?.uid ?? obj?.userId ?? obj?.user_id ?? '')
        if (idCandidate) setUserId(String(idCandidate))
        return
      }
      let guestId = window.sessionStorage.getItem(GUEST_ID_KEY)
      if (!guestId) {
        const uuid = (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`
        guestId = `guest-${uuid}`
        try { window.sessionStorage.setItem(GUEST_ID_KEY, guestId) } catch (_) {}
      }
      setUserId(guestId)
    } catch (_) {}
  }, [isVisible])

  useEffect(() => {
    const load = async () => {
      if (!isVisible) return
      
      setIsLoadingConvs(true)
      const controller = new AbortController()
      try {
        let eff = userId
        if (!eff) {
          try {
            const raw = localStorage.getItem('authUser') || window.sessionStorage.getItem('authUser')
            if (raw) {
              const obj = JSON.parse(raw)
              const idCandidate = (obj?.id ?? obj?.uid ?? obj?.userId ?? obj?.user_id ?? '')
              eff = idCandidate ? String(idCandidate) : ''
            }
          } catch (_) {}
        }
        if (!eff) {
          let guestId = window.sessionStorage.getItem(GUEST_ID_KEY)
          if (!guestId) {
            const uuid = (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`
            guestId = `guest-${uuid}`
            try { window.sessionStorage.setItem(GUEST_ID_KEY, guestId) } catch (_) {}
          }
          eff = guestId
          setUserId(guestId)
        }
        const list = await aiListConversations({ userId: eff, signal: controller.signal })
        setConversations(Array.isArray(list) ? list : [])
      } catch (_) {
        setConversations([])
      } finally {
        setIsLoadingConvs(false)
        controller.abort()
      }
    }
    load()
  }, [isVisible])

  useEffect(() => {
    if (!isVisible) return
    try {
      const raw = window.sessionStorage.getItem(JOB_KEY)
      if (raw) setJobId(String(raw))
    } catch (_) {}
  }, [isVisible])

  useEffect(() => {
    if (!isVisible) return
    try { window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(thread)) } catch (_) {}
  }, [thread, isVisible])

  useEffect(() => {
    if (endRef.current) endRef.current.scrollIntoView({ behavior: 'smooth' })
  }, [thread, isSending])

  useEffect(() => {
    if (!isVisible) return
    const onKey = (e) => {
      if (e.key === 'Escape') closeDrawer()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isVisible])

  const handleToggle = () => setIsVisible(v => !v)
  const toggleDrawer = () => setIsDrawerOpen(v => !v)
  const closeDrawer = () => setIsDrawerOpen(false)
  
  const handleCloseConversation = async () => {
    console.log('handleCloseConversation chamado:', { activeConversationId, userId })
    
    if (activeConversationId && userId) {
      console.log('Fazendo cleanup da conversa:', activeConversationId)
      try {
        await aiCleanupPublicacoesConversation({ conversationId: activeConversationId, userId })
        console.log('Cleanup realizado com sucesso')
      } catch (error) {
        console.warn('Erro ao fazer cleanup da conversa:', error)
      }
    } else {
      console.log('Não foi possível fazer cleanup - activeConversationId ou userId não disponíveis')
    }
    
    try { window.sessionStorage.removeItem(STORAGE_KEY) } catch (_) {}
    try { window.sessionStorage.removeItem(JOB_KEY) } catch (_) {}
    setJobId('')
    setThread([])
    setActiveConversationId('')
    setDraftText('')
    setAttachedFile(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
    
    setIsVisible(false)
  }

  const handleClear = async () => {
    console.log('handleClear chamado:', { activeConversationId, userId })
    
    if (abortCtrl) {
      try { abortCtrl.abort() } catch (_) {}
    }
    setIsSending(false)
    setAbortCtrl(null)
    
    if (activeConversationId && userId) {
      console.log('Fazendo cleanup da conversa:', activeConversationId)
      try {
        await aiCleanupPublicacoesConversation({ conversationId: activeConversationId, userId })
        console.log('Cleanup realizado com sucesso')
      } catch (error) {
        console.warn('Erro ao fazer cleanup da conversa:', error)
      }
    } else {
      console.log('Não foi possível fazer cleanup - activeConversationId ou userId não disponíveis')
    }
    
    try { window.sessionStorage.removeItem(STORAGE_KEY) } catch (_) {}
    try { window.sessionStorage.removeItem(JOB_KEY) } catch (_) {}
    setJobId('')
    setThread([])
    setActiveConversationId('')
    setDraftText('')
    setAttachedFile(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const openConversation = async (conversationId) => {
    setActiveConversationId(conversationId)
    try {
      const res = await aiGetConversationMessages({ userId, conversationId, limit: 50, order: 'asc' })
      const items = Array.isArray(res?.messages) ? res.messages : []
      const normalized = items.map(m => ({ id: m.id, role: m.role, content: m.text, ts: new Date(m.created_at).getTime() }))
      setThread(normalized)
    } catch (_) {
      setThread([])
    }
  }

  const adjustInputHeight = (el) => {
    if (!el) return
    try {
      const maxHeightPx = 200
      el.style.height = 'auto'
      const nextHeight = Math.min(el.scrollHeight, maxHeightPx)
      el.style.height = `${nextHeight}px`
      el.style.overflowY = el.scrollHeight > maxHeightPx ? 'auto' : 'hidden'
    } catch (_) {}
  }

  useEffect(() => {
    if (!isVisible) return
    adjustInputHeight(inputRef.current)
  }, [draftText, isVisible])

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleStop = () => {
    if (abortCtrl) {
      try { abortCtrl.abort() } catch (_) {}
    }
    setIsSending(false)
    setAbortCtrl(null)
  }

  const formatTime = (ts) => {
    try { return new Date(ts).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) } catch { return '' }
  }

  const openFilePicker = () => {
    if (isSending) return
    if (fileInputRef.current) fileInputRef.current.click()
  }

  const handleFileChange = (e) => {
    const file = e?.target?.files?.[0] || null
    setAttachedFile(file)
  }

  const handleRemoveFile = () => {
    setAttachedFile(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleSend = async () => {
    const text = draftText.trim()
    if ((!text && !attachedFile) || isSending) return
    if (text.length > 500) {
      const tooLong = { id: `m-${Date.now()}-e`, role: 'assistant', content: 'Sua mensagem excede 500 caracteres. Por favor, reduza o texto.', ts: Date.now() }
      setThread(prev => [...prev, tooLong])
      return
    }

    let effectiveUserId = userId
    if (!effectiveUserId) {
      try {
        const raw = localStorage.getItem('authUser') || window.sessionStorage.getItem('authUser')
        if (raw) {
          const obj = JSON.parse(raw)
          const idCandidate = (obj?.id ?? obj?.uid ?? obj?.userId ?? obj?.user_id ?? '')
          effectiveUserId = idCandidate ? String(idCandidate) : ''
        }
        if (!effectiveUserId) {
          let guestId = window.sessionStorage.getItem(GUEST_ID_KEY)
          if (!guestId) {
            const uuid = (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`
            guestId = `guest-${uuid}`
            try { window.sessionStorage.setItem(GUEST_ID_KEY, guestId) } catch (_) {}
          }
          effectiveUserId = guestId
        }
      } catch (_) {}
    }
    if (!effectiveUserId) {
      const noUser = { id: `m-${Date.now()}-e`, role: 'assistant', content: 'Não foi possível identificar o usuário (user_id). Recarregue a página e tente novamente.', ts: Date.now() }
      setThread(prev => [...prev, noUser])
      return
    }

    let composedContent = text
    if (attachedFile) {
      const fileNote = `(Anexo: ${attachedFile.name})`
      composedContent = composedContent ? `${composedContent}\n\n${fileNote}` : fileNote
    }
    const userMsg = { id: `m-${Date.now()}-u`, role: 'user', content: composedContent, ts: Date.now() }
    setThread(prev => [...prev, userMsg])
    setDraftText('')
    setIsSending(true)

    const controller = new AbortController()
    setAbortCtrl(controller)
    const autoAbortId = setTimeout(() => {
      try { controller.abort() } catch (_) {}
    }, MAX_WAIT_MS)

    try {
      const isQualificacaoPage = location.pathname.includes('/qualificacao-detail/')
      
      if (isQualificacaoPage && processoContext) {
        if (attachedFile) {
          // Na página de qualificação, não criamos conversas nem salvamos no histórico
          let conversationId = null
          
          const postResp = await aiSendPublicacoesMessage({ 
            message: text, 
            userId: effectiveUserId, 
            idProcesso: processoContext.qualificacaoId, // Para a API
            numeroProcesso: processoContext.numeroCNJ, // Para o nome do arquivo no Azure
            conversationId: conversationId || null,
            newConversation: !conversationId,
            file: attachedFile,
            signal: controller.signal 
          })
          
          const newId = postResp?.id || ''
          if (newId && newId !== jobId) {
            setJobId(newId)
            try { window.sessionStorage.setItem(JOB_KEY, newId) } catch (_) {}
          }

          const final = await aiWaitForResult(newId || jobId, { userId: effectiveUserId, pollIntervalMs: 800, maxWaitMs: MAX_WAIT_MS, signal: controller.signal })
          const status = (final?.status || '').toLowerCase()
          let answer = ''
          if (status === 'failed') {
            answer = 'Não foi possível processar a mensagem.'
          } else {
            answer = aiExtractLatestText(final) || 'Sem conteúdo.'
          }
          const botMsg = { id: `m-${Date.now()}-a`, role: 'assistant', content: answer, ts: Date.now() }
          setThread(prev => [...prev, botMsg])
        } else {
          // Na página de qualificação, não criamos conversas nem salvamos no histórico
          let conversationId = null
          
          const postResp = await aiSendQualificacaoMessage({ 
            message: text, 
            userId: effectiveUserId, 
            idProcesso: processoContext.qualificacaoId, // Usar qualificacaoId em vez de numeroCNJ
            conversationId: conversationId || '',
            signal: controller.signal 
          })
          
          const newId = postResp?.id || ''
          if (newId && newId !== jobId) {
            setJobId(newId)
            try { window.sessionStorage.setItem(JOB_KEY, newId) } catch (_) {}
          }

          const final = await aiWaitForQualificacaoResult(newId || jobId, { userId: effectiveUserId, pollIntervalMs: 800, maxWaitMs: MAX_WAIT_MS, signal: controller.signal })
          const status = (final?.status || '').toLowerCase()
          let answer = ''
          if (status === 'failed') {
            answer = 'Não foi possível processar a mensagem.'
          } else {
            answer = aiExtractLatestText(final) || 'Sem conteúdo.'
          }
          const botMsg = { id: `m-${Date.now()}-a`, role: 'assistant', content: answer, ts: Date.now() }
          setThread(prev => [...prev, botMsg])
        }
      } else {
        let conversationId = activeConversationId
        if (!conversationId) {
          try {
            const titleFromQuestion = text.replace(/\s+/g, ' ').trim().slice(0, 80)
            const created = await aiCreateConversation({ userId: effectiveUserId, title: titleFromQuestion || 'Novo chat', signal: controller.signal })
            conversationId = created?.id || ''
            setActiveConversationId(conversationId)
            aiListConversations({ userId: effectiveUserId }).then(setConversations).catch(() => {})
          } catch (_) {}
        }

        const postResp = await aiSendMessage({ 
          message: text, 
          userId: effectiveUserId, 
          conversationId, 
          jobId, 
          file: attachedFile, 
          numeroProcesso: processoContext?.numeroCNJ || null,
          signal: controller.signal 
        })
        const newId = postResp?.id || ''
        if (newId && newId !== jobId) {
          setJobId(newId)
          try { window.sessionStorage.setItem(JOB_KEY, newId) } catch (_) {}
        }

        const final = await aiWaitForResult(newId || jobId, { userId: effectiveUserId, pollIntervalMs: 800, maxWaitMs: MAX_WAIT_MS, signal: controller.signal })
        const status = (final?.status || '').toLowerCase()
        let answer = ''
        if (status === 'failed') {
          answer = 'Não foi possível processar a mensagem.'
        } else {
          answer = aiExtractLatestText(final) || 'Sem conteúdo.'
        }
        const botMsg = { id: `m-${Date.now()}-a`, role: 'assistant', content: answer, ts: Date.now() }
        setThread(prev => [...prev, botMsg])
        // Só atualiza a lista de conversas se não estiver na página de qualificação
        const isQualificacaoPage = location.pathname.includes('/qualificacao-detail/')
        if (!isQualificacaoPage) {
          aiListConversations({ userId: effectiveUserId }).then(setConversations).catch(() => {})
        }
      }
    } catch (e) {
      console.log('console error', e)
      if (e?.name === 'AbortError') {
        const stopMsg = { id: `m-${Date.now()}-s`, role: 'assistant', content: 'Resposta interrompida.', ts: Date.now() }
        setThread(prev => [...prev, stopMsg])
      } else {
        
        const errMsg = { id: `m-${Date.now()}-e`, role: 'assistant', content: 'Não consegui responder agora. Tente novamente.', ts: Date.now() }
        setThread(prev => [...prev, errMsg])
      }
    } finally {
      setIsSending(false)
      setAbortCtrl(null)
      clearTimeout(autoAbortId)
      setAttachedFile(null)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  return (
    <div className="aiDock" aria-live="polite">
      <button
        type="button"
        className="aiLauncher"
        onClick={handleToggle}
        aria-label={isVisible ? 'Fechar chat' : 'Abrir chat'}
        title={isVisible ? 'Fechar' : 'Chat'}
      >
        {isVisible ? '×' : (
          <div>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-sparkle-icon lucide-sparkle"><path d="M11.017 2.814a1 1 0 0 1 1.966 0l1.051 5.558a2 2 0 0 0 1.594 1.594l5.558 1.051a1 1 0 0 1 0 1.966l-5.558 1.051a2 2 0 0 0-1.594 1.594l-1.051 5.558a1 1 0 0 1-1.966 0l-1.051-5.558a2 2 0 0 0-1.594-1.594l-5.558-1.051a1 1 0 0 1 0-1.966l5.558-1.051a2 2 0 0 0 1.594-1.594z"/></svg>
            <span style={{ marginLeft: '5px' }}>Pergunte ao Blops AI</span>
          </div>
        )}
      </button>

      {isVisible && (
        <section className="aiPanel" role="dialog" aria-modal="true" aria-label="Chat Blops AI">
          <header className="aiHeader" style={{ borderBottomColor: primaryColor }}>
            <div className="aiHeader__left">
              <button className="aiIconBtn aiHamburger" onClick={toggleDrawer} title="Menu" aria-label="Abrir menu">☰</button>
              <div className="aiHeader__title">
                <span className="aiHeader__status" />
                <span>{title}</span>
              </div>
            </div>
            <div className="aiHeader__actions">
              <button className="aiIconBtn" onClick={handleClear} title="Limpar conversa" aria-label="Limpar conversa">↺</button>
              <button className="aiIconBtn" onClick={handleCloseConversation} title="Fechar conversa" aria-label="Fechar conversa">×</button>
            </div>
          </header>

          <div className="aiMainWrap">
            <aside className="aiSidebar" role="navigation" aria-label="Menu lateral">
              <div className="aiSideSection">
                <div className="aiSideTitle">Nova conversa</div>
                <button className="aiSideItem" type="button" onClick={handleClear}>
                  <span className="aiSideArrow">→</span>
                  <span>Iniciar nova conversa</span>
                </button>
              </div>
              <div className="aiSideDivider" />
              <div className="aiSideSection">
                <div className="aiSideTitle">Conversas recentes</div>
                {isLoadingConvs ? (
                  <div className="aiSideEmpty">Carregando…</div>
                ) : (!conversations || conversations.length === 0) ? (
                  <div className="aiSideEmpty">Sem conversas</div>
                ) : (
                  <ul className="aiSideList" role="list">
                    {conversations.map(c => (
                      <li key={c.id}>
                        <button
                          type="button"
                          className={`aiSideItem ${activeConversationId === c.id ? 'is-active' : ''}`}
                          onClick={() => openConversation(c.id)}
                          title={c.title || 'Novo chat'}
                        >
                          <span className="aiSideArrow">↳</span>
                          <span className="aiSideConversation">
                            <span className="aiSideConvTitle">{c.title || 'Novo chat'}</span>
                            {c.last_message_preview && (
                              <span className="aiSideConvPreview">{c.last_message_preview}</span>
                            )}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </aside>

                <div className={`aiDrawerOverlay ${isDrawerOpen ? 'is-open' : ''}`} onClick={closeDrawer} aria-hidden />
                <aside className={`aiDrawer ${isDrawerOpen ? 'is-open' : ''}`} role="dialog" aria-modal="true" aria-label="Menu do chat">
                  <div className="aiSideSection" style={{ padding: 16 }}>
                    <div className="aiSideTitle">Nova conversa</div>
                    <button className="aiSideItem" type="button" onClick={() => { handleClear(); closeDrawer(); }}>
                      <span className="aiSideArrow">→</span>
                      <span>Iniciar nova conversa</span>
                    </button>
                  </div>
                  <div className="aiSideDivider" />
                  <div className="aiSideSection" style={{ padding: 16, flex: 1, overflowY: 'auto' }}>
                    <div className="aiSideTitle">Conversas recentes</div>
                    {isLoadingConvs ? (
                      <div className="aiSideEmpty">Carregando…</div>
                    ) : (!conversations || conversations.length === 0) ? (
                      <div className="aiSideEmpty">Sem conversas</div>
                    ) : (
                      <ul className="aiSideList" role="list">
                        {conversations.map(c => (
                          <li key={c.id}>
                            <button
                              type="button"
                              className={`aiSideItem ${activeConversationId === c.id ? 'is-active' : ''}`}
                              onClick={() => { openConversation(c.id); closeDrawer(); }}
                              title={c.title || 'Novo chat'}
                            >
                              <span className="aiSideArrow">↳</span>
                              <span className="aiSideConversation">
                                <span className="aiSideConvTitle">{c.title || 'Novo chat'}</span>
                                {c.last_message_preview && (
                                  <span className="aiSideConvPreview">{c.last_message_preview}</span>
                                )}
                              </span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </aside>

            <div className="aiMain">
              <main className="aiBody">
            {thread.map(m => (
              <article key={m.id} className={`aiMsg aiMsg--${m.role}`}>
                {m.role === 'assistant' && <div className="aiAvatar" aria-hidden>🤖</div>}
                <div className="aiBubble aiBubble--" data-role={m.role}>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
                  <div className="aiMeta">{formatTime(m.ts)}</div>
                </div>
                {m.role === 'user' && <div className="aiAvatar aiAvatar--user" aria-hidden>👤</div>}
              </article>
            ))}
            {isSending && (
              <article className="aiMsg aiMsg--assistant">
                <div className="aiAvatar" aria-hidden>🤖</div>
                <div className="aiBubble"><span className="aiTyping" /></div>
              </article>
            )}
            {thread.length === 0 && (
              <div className="aiSuggestions">
                {processoContext && (
                  <div style={{ 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                    color: 'white', 
                    padding: '12px 16px', 
                    marginBottom: '20px',
                    borderRadius: '8px'
                  }}>
                    <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '4px' }}>
                      Processo {processoContext.numeroCNJ}
                    </div>
                    <div style={{ fontSize: '12px', opacity: 0.9 }}>
                      {processoContext.reclamante} X {processoContext.reuRazaoSocial}
                    </div>
                  </div>
                )}
                
                <h3 style={{
                  textAlign: 'center',
                  fontSize: '32px',
                  fontWeight: 'normal',
                  paddingBottom: '20px'
                }} className="aiTextGradient">
                  {processoContext ? 'Como posso ajudar com este processo?' : 'Como posso ajudar hoje?'}
                </h3>

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  paddingBottom: '20px'
                }}>
                  <h3>Sugestões</h3>
                <div className="aiSuggestionsGrid">
                  {processoContext ? (
                    <>
                      <div className="aiSuggestionCard" onClick={() => setDraftText(`Analise este processo ${processoContext.numeroCNJ} e me dê um resumo das principais informações.`)}>
                        Analise este processo e me dê um resumo
                      </div>
                      <div className="aiSuggestionCard" onClick={() => setDraftText(`Qual o status atual do processo ${processoContext.numeroCNJ}?`)}>
                        Qual o status atual deste processo?
                      </div>
                      <div className="aiSuggestionCard" onClick={() => setDraftText(`Quais são os próximos passos recomendados para o processo ${processoContext.numeroCNJ}?`)}>
                        Quais são os próximos passos recomendados?
                      </div>
                      <div className="aiSuggestionCard" onClick={() => setDraftText(`Existe algum risco identificado no processo ${processoContext.numeroCNJ}?`)}>
                        Existe algum risco identificado?
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="aiSuggestionCard" onClick={() => setDraftText('Qual o último lead analisado?')}>
                        Qual o último lead analisado?
                      </div>
                      <div className="aiSuggestionCard" onClick={() => setDraftText('qual a Importância Segurada (IS) para as apólices cadastradas?')}>
                        qual a Importância Segurada (IS) para as apólices cadastradas?
                      </div>
                      <div className="aiSuggestionCard" onClick={() => setDraftText('Quantas apólices temos?')}>
                        Quantas apólices temos?
                      </div>
                    </>
                  )}
                </div>
                </div>
              </div>
            )}
            <div ref={endRef} />
              </main>

              <footer className="aiFooter">
                <div className="aiComposer">
              <div className="aiInputCard">
                <div className="aiInputTop">
                  <textarea
                    ref={inputRef}
                    className="aiInput"
                    placeholder="Escreva sua mensagem..."
                    value={draftText}
                    onChange={e => { setDraftText(e.target.value); adjustInputHeight(e.target) }}
                    onKeyDown={handleKeyDown}
                    rows={1}
                    aria-label="Mensagem"
                    style={{ overflow: 'hidden', resize: 'none', fontSize: '14px' }}
                  />
                  {attachedFile && (
                    <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}>
                      <span style={{ padding: '4px 8px', background: '#f1f3f5', borderRadius: '12px', maxWidth: '70%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={attachedFile.name}>
                        {attachedFile.name}
                      </span>
                      <button type="button" className="aiIconBtn" onClick={handleRemoveFile} aria-label="Remover anexo" title="Remover anexo">×</button>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className="aiSendGhost"
                  onClick={handleSend}
                  aria-label="Enviar"
                  disabled={(!draftText.trim() && !attachedFile) || isSending}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-arrow-up"><path d="m5 12 7-7 7 7"/><path d="M12 19V5"/></svg>
                </button>
                <div className="aiInputDivider" />
                <div className="aiInputBottom">
                  <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleFileChange} aria-hidden />
                  <button className="aiAttachBtn" type="button" aria-label="Anexar arquivo" onClick={openFilePicker} disabled={isSending} title="Anexar arquivo">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-paperclip-icon lucide-paperclip"><path d="m16 6-8.414 8.586a2 2 0 0 0 2.829 2.829l8.414-8.586a4 4 0 1 0-5.657-5.657l-8.379 8.551a6 6 0 1 0 8.485 8.485l8.379-8.551"/></svg>
                  </button>
                </div>
              </div>
                  {isSending && (
                    <button type="button" className="aiBtn aiBtn--danger" onClick={handleStop}>Parar</button>
                  )}
                </div>
              </footer>
            </div>
          </div>
        </section>
      )}
    </div>
  )
}

export default ChatWidgetControlled
